import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcImportEngine.css";
import { buildDefaultState, buildPreparedData, loadEngineState, recomputeEngineState, saveEngineState, saveEngineStateDurable, saveSharedLogicAcrossScopes, detectUniversalImportProfile } from "../utils/mcEngineData";
import { canAccessAdminTabs, useCurrentMcProfile } from "../utils/profileAccess";
import { MC_CSV_SITE_RESOLUTION_KEY, MC_SITES_KEY, detectSiteHeaders, inventoryHeaderFromSiteName, labelFromSiteValue, loadSites, normalizeSiteId, resolveSite, saveCsvHeaderSiteMappings, saveSites } from "../utils/mcOrgData";
import { mcGetItem, mcSetCloudItem, mcSetCloudItemImmediate, mcSetItem } from "../utils/mcCloudSync";
import { DEFAULT_POS_SETTINGS, POS_ENGINE_HEADERS, loadPosSettings, loadPreparedInventory, savePosSettings, savePreparedInventory, buildEngineFromPreparedInventory } from "../utils/mcPosIntegration";
import { commitActiveInventorySnapshot, loadActiveInventorySnapshot, subscribeActiveInventorySnapshot } from "../utils/mcActiveInventory";

const FIELD_LABELS = [
  ["product", "Product"],
  ["category", "Category"],
  ["cost", "Cost"],
  ["systemSoh", "System SOH"],
  ["sku", "SKU"],
  ["site", "Site / Store"],
];

function safeJsonParse(raw, fallback = null) {
  try { return raw ? JSON.parse(raw) : fallback; } catch (_) { return fallback; }
}

function mappingTimestamp(state = {}) {
  const raw = state?.importSourceProfile?.mappingConfirmedAt || state?.mappingConfirmedAt || "";
  const ms = raw ? Date.parse(raw) : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function mergeMappingAuthority(primary = {}, fallback = {}) {
  const primaryTs = mappingTimestamp(primary);
  const fallbackTs = mappingTimestamp(fallback);
  if (primaryTs && primaryTs >= fallbackTs) return primary.mapping || primary.csvMapping || fallback.mapping || fallback.csvMapping || {};
  if (fallbackTs) return fallback.mapping || fallback.csvMapping || primary.mapping || primary.csvMapping || {};
  return primary.mapping || primary.csvMapping || fallback.mapping || fallback.csvMapping || {};
}

export default function MorpheusCountMapping() {
  const currentProfile = useCurrentMcProfile();
  const [engine, setEngine] = useState(buildDefaultState());
  const [siteMappings, setSiteMappings] = useState({});
  const [siteMappingSavedAt, setSiteMappingSavedAt] = useState("");
  const [siteResolutionBusy, setSiteResolutionBusy] = useState(false);
  const [siteResolutionNotice, setSiteResolutionNotice] = useState("");
  const [rowSiteMappings, setRowSiteMappings] = useState({});
  const [durableSiteResolution, setDurableSiteResolution] = useState(() => safeJsonParse(mcGetItem(MC_CSV_SITE_RESOLUTION_KEY), {}));
  const [posSettings, setPosSettings] = useState(() => loadPosSettings("ALL"));
  const [posInventory, setPosInventory] = useState(() => loadPreparedInventory("ALL"));

  useEffect(() => {
    let active = true;
    const hydrateSharedImport = async () => {
      const stored = loadEngineState("ALL") || buildDefaultState("ALL");
      const snapshot = await loadActiveInventorySnapshot("ALL", { refreshCloud: true }).catch(() => null);
      if (!active) return;
      const merged = snapshot
        ? { ...stored, ...snapshot, config: stored.config || snapshot.config, rules: stored.rules || snapshot.rules, mapping: mergeMappingAuthority(stored, snapshot), csvMapping: mergeMappingAuthority(stored, snapshot) }
        : stored;
      setEngine(merged);
      setPosSettings(loadPosSettings("ALL"));
      setPosInventory(loadPreparedInventory("ALL"));
    };
    hydrateSharedImport();
    const unsubscribe = subscribeActiveInventorySnapshot("ALL", (snapshot) => {
      if (!active || !snapshot) return;
      setEngine((current) => ({ ...current, ...snapshot, config: current.config || snapshot.config, rules: current.rules || snapshot.rules, mapping: mergeMappingAuthority(current, snapshot), csvMapping: mergeMappingAuthority(current, snapshot) }));
    });
    return () => { active = false; if (typeof unsubscribe === "function") unsubscribe(); };
  }, []);

  const headers = engine.parsedHeaders || [];
  const sourceProfile = useMemo(() => {
    const detected = detectUniversalImportProfile(headers, engine.rawRows || [], engine?.config?.storeName || "");
    const effectiveFields = { ...(detected.fields || {}) };
    Object.keys(effectiveFields).forEach((key) => {
      const selectedHeader = (engine.mapping || engine.csvMapping || {})[key];
      if (selectedHeader) effectiveFields[key] = { ...(effectiveFields[key] || {}), header: selectedHeader, confidence: 100, source: "manual" };
    });
    const candidateSiteHeader = (engine.mapping || engine.csvMapping || {}).site || detected.rowSiteHeader || "";
    const inventoryHeaders = Array.isArray(detected.inventoryHeaders) ? detected.inventoryHeaders : [];
    const selectedSiteHeader = candidateSiteHeader && !inventoryHeaders.includes(candidateSiteHeader) ? candidateSiteHeader : "";
    const layout = inventoryHeaders.length > 1 ? "column_site" : (selectedSiteHeader ? "row_site" : detected.layout);
    const unresolvedRequired = ["product", "systemSoh"].filter((key) => !effectiveFields[key]?.header);
    if (layout === "row_site" && !selectedSiteHeader) unresolvedRequired.push("site");
    return {
      ...detected,
      ...(engine.importSourceProfile || {}),
      fields: effectiveFields,
      rowSiteHeader: selectedSiteHeader,
      layout,
      unresolvedRequired,
      readyForNormalization: unresolvedRequired.length === 0,
    };
  }, [headers.join("|"), engine.rawRows?.length, JSON.stringify(engine.mapping || {}), JSON.stringify(engine.csvMapping || {})]);
  const rawCsvMapping = String(engine?.config?.dataSourceMode || "csv").toLowerCase() === "pos"
    ? (engine.csvMapping || {})
    : (engine.mapping || {});
  const csvMapping = sourceProfile.layout === "column_site" && (sourceProfile.inventoryHeaders || []).includes(rawCsvMapping.site)
    ? { ...rawCsvMapping, site: "" }
    : rawCsvMapping;
  const detectedSiteHeaders = useMemo(() => sourceProfile.layout === "column_site" ? detectSiteHeaders(headers) : [], [headers.join("|"), sourceProfile.layout]);
  const detectedRowSiteValues = useMemo(() => {
    if (sourceProfile.layout !== "row_site" || !sourceProfile.rowSiteHeader) return [];
    return Array.from(new Set((engine.rawRows || [])
      .map((row) => String(row?.[sourceProfile.rowSiteHeader] || "").trim())
      .filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  }, [sourceProfile.layout, sourceProfile.rowSiteHeader, engine.rawRows]);
  const siteOptions = useMemo(() => loadSites().filter((site) => site.active !== false), [headers.length, siteMappingSavedAt]);
  const optionsForField = () => headers;
  const hasPosSource = useMemo(() => {
    if ((posInventory.rows || []).length > 0) return true;
    if (String(engine?.config?.dataSourceMode || "").toLowerCase() !== "pos") return false;
    return (engine.parsedHeaders || []).length > 0 || (engine.rawRows || []).length > 0;
  }, [posInventory.rows?.length, engine?.config?.dataSourceMode, (engine.parsedHeaders || []).join("|"), engine.rawRows?.length]);
  const posHeaders = useMemo(() => {
    if (!hasPosSource) return [];
    const set = new Set(POS_ENGINE_HEADERS);
    (posInventory.rows || []).forEach((row) => Object.keys(row || {}).forEach((key) => set.add(key)));
    if (String(engine?.config?.dataSourceMode || "").toLowerCase() === "pos") {
      (engine.parsedHeaders || []).forEach((header) => set.add(header));
      (engine.rawRows || []).forEach((row) => Object.keys(row || {}).forEach((key) => set.add(key)));
    }
    return Array.from(set).filter(Boolean);
  }, [hasPosSource, posInventory.rows?.length, engine?.config?.dataSourceMode, (engine.parsedHeaders || []).join("|")]);
  const optionsForPosField = () => posHeaders;

  const normalizeMatch = (value = "") => String(value || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();
  const matchTokens = (value = "") => new Set(normalizeMatch(value).split(/\s+/).filter(Boolean));
  const siteSimilarity = (sourceValue = "", site = {}) => {
    const source = normalizeMatch(sourceValue);
    const candidates = [site.name, site.storeKey, site.siteId, ...(site.aliases || [])].filter(Boolean);
    let best = 0;
    candidates.forEach((candidate) => {
      const target = normalizeMatch(candidate);
      if (!source || !target) return;
      if (source === target) { best = Math.max(best, 100); return; }
      if (source.includes(target) || target.includes(source)) best = Math.max(best, 88);
      const a = matchTokens(source); const b = matchTokens(target);
      const overlap = [...a].filter((token) => b.has(token)).length;
      const union = new Set([...a, ...b]).size || 1;
      best = Math.max(best, Math.round((overlap / union) * 100));
    });
    return best;
  };
  const bestSiteSuggestion = (value = "") => {
    const ranked = siteOptions.map((site) => ({ site, confidence: siteSimilarity(value, site) })).sort((a, b) => b.confidence - a.confidence);
    return ranked[0] || { site: null, confidence: 0 };
  };

  useEffect(() => {
    if (!detectedRowSiteValues.length) { setRowSiteMappings({}); return; }
    setRowSiteMappings((previous) => {
      const next = { ...previous };
      const engineResolution = engine?.importSourceProfile?.siteResolution || {};
      const durableResolution = durableSiteResolution || {};
      const sameDurableSource = !durableResolution.sourceHeader || durableResolution.sourceHeader === sourceProfile.rowSiteHeader;
      const savedValues = [
        ...(sameDurableSource && Array.isArray(durableResolution.values) ? durableResolution.values : []),
        ...(Array.isArray(engineResolution.values) ? engineResolution.values : []),
      ];
      const savedByValue = new Map(savedValues.map((item) => [String(item?.sourceValue || "").trim(), item]));

      detectedRowSiteValues.forEach((value) => {
        const saved = savedByValue.get(value);
        if (saved && ["map", "ignore"].includes(saved.action)) {
          next[value] = { action: saved.action, siteId: saved.siteId || "", confidence: Number(saved.confidence || 100), auto: saved.auto !== false };
          return;
        }
        if (next[value] && ["map", "ignore"].includes(next[value].action)) return;
        const suggestion = bestSiteSuggestion(value);
        next[value] = suggestion.confidence >= 92
          ? { action: "map", siteId: suggestion.site?.siteId || "", confidence: suggestion.confidence, auto: true }
          : { action: "review", siteId: suggestion.confidence >= 45 ? (suggestion.site?.siteId || "") : "", confidence: suggestion.confidence, auto: false };
      });
      Object.keys(next).forEach((value) => { if (!detectedRowSiteValues.includes(value)) delete next[value]; });
      return next;
    });
  }, [detectedRowSiteValues.join("|"), siteOptions.map((site) => `${site.siteId}:${site.name}`).join("|"), sourceProfile.rowSiteHeader, JSON.stringify(durableSiteResolution || {}), JSON.stringify(engine?.importSourceProfile?.siteResolution || {})]);

  useEffect(() => {
    if (!detectedSiteHeaders.length) {
      setSiteMappings({});
      return;
    }

    setSiteMappings((prev) => {
      const next = { ...prev };
      let changed = false;
      detectedSiteHeaders.forEach((header) => {
        if (next[header] !== undefined) return;
        const resolved = resolveSite(header, headers);
        next[header] = resolved?.siteId || "__IGNORE__";
        changed = true;
      });
      Object.keys(next).forEach((header) => {
        if (!detectedSiteHeaders.includes(header)) {
          delete next[header];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [detectedSiteHeaders.join("|"), headers.join("|")]);

  const unresolvedSiteHeaders = detectedSiteHeaders.filter((header) => {
    const selected = siteMappings[header];
    if (!selected || selected === "__IGNORE__") return true;
    return !siteOptions.some((site) => site.siteId === selected);
  });
  const unresolvedRowSites = detectedRowSiteValues.filter((value) => {
    const resolution = rowSiteMappings[value];
    return !resolution || resolution.action === "review" || (resolution.action === "map" && !siteOptions.some((site) => site.siteId === resolution.siteId));
  });
  const mappedRowSites = detectedRowSiteValues.filter((value) => !unresolvedRowSites.includes(value));

  const saveSiteMappings = async () => {
    const payload = {};
    detectedSiteHeaders.forEach((header) => {
      const selected = siteMappings[header];
      if (!selected || selected === "__IGNORE__") return;
      payload[header] = selected;
    });
    saveCsvHeaderSiteMappings(payload);
    const now = new Date().toISOString();
    const durableResolution = {
      orgScoped: true,
      updatedAt: now,
      sourceLayout: "column_site",
      sourceHeaders: detectedSiteHeaders,
      aliases: payload,
    };
    const durableRaw = JSON.stringify(durableResolution);
    mcSetItem(MC_CSV_SITE_RESOLUTION_KEY, durableRaw);
    setDurableSiteResolution(durableResolution);
    const [resolutionSaved, sitesSaved] = await Promise.all([
      mcSetCloudItemImmediate(MC_CSV_SITE_RESOLUTION_KEY, durableRaw),
      mcSetCloudItemImmediate(MC_SITES_KEY, JSON.stringify(loadSites())),
    ]);
    if (!resolutionSaved || !sitesSaved) {
      setSiteResolutionNotice("CSV site aliases could not be confirmed in this organisation. Please retry.");
      return;
    }
    setSiteMappingSavedAt(now);
    setSiteResolutionNotice("CSV site aliases saved for this organisation.");
  };

  const saveRowSiteMappings = async () => {
    if (unresolvedRowSites.length || siteResolutionBusy) return;
    setSiteResolutionBusy(true);
    setSiteResolutionNotice("Saving site resolution and reprocessing this import…");
    try {
      const aliases = {};
      detectedRowSiteValues.forEach((value) => {
        const resolution = rowSiteMappings[value];
        if (resolution?.action === "map" && resolution.siteId) aliases[value] = resolution.siteId;
      });
      saveCsvHeaderSiteMappings(aliases);
      const now = new Date().toISOString();
      const nextProfile = {
        ...sourceProfile,
        siteResolution: {
          updatedAt: now,
          sourceHeader: sourceProfile.rowSiteHeader,
          values: detectedRowSiteValues.map((value) => ({ sourceValue: value, ...(rowSiteMappings[value] || {}) })),
          ready: true,
        },
      };
      const durableResolution = {
        orgScoped: true,
        updatedAt: now,
        sourceHeader: sourceProfile.rowSiteHeader,
        sourceLayout: sourceProfile.layout,
        values: nextProfile.siteResolution.values,
      };
      const durableRaw = JSON.stringify(durableResolution);
      mcSetItem(MC_CSV_SITE_RESOLUTION_KEY, durableRaw);
      setDurableSiteResolution(durableResolution);
      const resolutionSaved = await mcSetCloudItemImmediate(MC_CSV_SITE_RESOLUTION_KEY, durableRaw);
      if (!resolutionSaved) throw new Error("Site resolution could not be confirmed in the organisation cloud record.");

      // CSV aliases are stored on the organisation's site registry. Confirm that
      // registry immediately too, so a refresh cannot replay an older org snapshot.
      const sitesSaved = await mcSetCloudItemImmediate(MC_SITES_KEY, JSON.stringify(loadSites()));
      if (!sitesSaved) throw new Error("Site aliases could not be confirmed in the organisation site registry.");

      const nextEngine = { ...engine, importSourceProfile: nextProfile, sourceLayout: nextProfile.layout, normalizationStatus: nextProfile.readyForNormalization ? "ready" : "mapping_required", updatedAt: now };
      setEngine(nextEngine);
      saveEngineState(nextEngine, "ALL");
      await commitActiveInventorySnapshot("ALL", nextEngine);

      const results = [];
      for (const value of detectedRowSiteValues) {
        const resolution = rowSiteMappings[value] || {};
        if (resolution.action === "ignore") {
          results.push({ site: value, label: value, status: "skipped", skipped: true, reason: "Ignored by site resolution" });
          continue;
        }
        const site = siteOptions.find((item) => item.siteId === resolution.siteId);
        if (!site) {
          results.push({ site: value, label: value, status: "failed", reason: "Resolved MatrixOS site no longer exists" });
          continue;
        }
        const rows = (nextEngine.rawRows || []).filter((row) => String(row?.[nextProfile.rowSiteHeader] || "").trim() === value);
        const mapping = { ...(nextEngine.mapping || {}), site: nextProfile.rowSiteHeader };
        const preparedRows = buildPreparedData(rows, mapping, nextEngine.config, nextEngine.rules);
        const siteImportId = `${nextEngine.activeImportId || nextEngine.importId || `import-${Date.now()}`}__${site.siteId}`;
        const siteSnapshot = {
          ...nextEngine,
          scope: site.siteId,
          importId: siteImportId,
          activeImportId: siteImportId,
          rawRows: rows,
          preparedRows,
          mapping,
          rowCount: rows.length,
          updatedAt: now,
          distributionContext: { mode: "SITE", scope: site.siteId, inheritedFrom: "ALL", sourceSiteValue: value },
        };
        const committed = await commitActiveInventorySnapshot(site.siteId, siteSnapshot);
        if (committed?.ok) {
          const lightweight = { ...siteSnapshot, csvText: "", rawRows: [], preparedRows: [], parsedHeaders: [], importSnapshotRef: { importId: siteImportId, scope: site.siteId, rowCount: rows.length, columnCount: headers.length, uploadedAt: nextEngine.uploadedAt || now, status: "ready" } };
          saveEngineState(lightweight, site.siteId);
          savePreparedInventory(preparedRows, site.siteId, { source: "universal-csv-site-resolution", provider: "csv", syncedAt: now });
          results.push({ site: site.siteId, label: site.name || value, status: "ready", ok: true, rowCount: rows.length, reason: "" });
        } else {
          results.push({ site: site.siteId, label: site.name || value, status: "failed", reason: committed?.reason || "Snapshot commit failed" });
        }
      }

      const succeeded = results.filter((item) => item.status === "ready").length;
      const skipped = results.filter((item) => item.status === "skipped").length;
      const failed = results.filter((item) => item.status === "failed").length;
      let history = [];
      try { history = JSON.parse(mcGetItem("mc-global-distribution-history") || "[]"); } catch (_) { history = []; }
      const existingBatch = (Array.isArray(history) ? history : []).find((item) => item.fileName === (nextEngine.fileName || "CSV import") && ["completed_with_skips", "partial", "queued"].includes(item.status));
      const batchId = existingBatch?.batchId || `site-resolution-${Date.now()}`;
      const batch = {
        ...(existingBatch || {}),
        batchId,
        status: failed ? "partial" : skipped ? "completed_with_skips" : "completed",
        requestedAt: now,
        completedAt: new Date().toISOString(),
        fileName: nextEngine.fileName || "CSV import",
        sourceScope: "ALL",
        total: results.length,
        succeeded,
        skipped,
        failed,
        results,
      };
      const nextHistory = [batch, ...(Array.isArray(history) ? history.filter((item) => item.batchId !== batchId) : [])].slice(0, 20);
      const serialized = JSON.stringify(nextHistory);
      try { mcSetItem("mc-global-distribution-history", serialized); } catch (_) {}
      mcSetCloudItem("mc-global-distribution-history", serialized);
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("mc-distribution-history-updated", { detail: nextHistory }));

      setSiteMappingSavedAt(now);
      setSiteResolutionNotice(`Site resolution complete: ${succeeded} ready${skipped ? `, ${skipped} skipped` : ""}${failed ? `, ${failed} failed` : ""}.`);
    } catch (error) {
      console.error("Site resolution reprocessing failed", error);
      setSiteResolutionNotice(error?.message || "Site resolution could not be completed.");
    } finally {
      setSiteResolutionBusy(false);
    }
  };
  const createSiteFromRowValue = (value) => {
    const siteId = normalizeSiteId(value);
    const existing = loadSites();
    if (!existing.some((site) => site.siteId === siteId)) {
      saveSites([...existing, { siteId, name: labelFromSiteValue(value), storeKey: inventoryHeaderFromSiteName(value), aliases: [value, labelFromSiteValue(value)], active: true, billingEnabled: true, source: "universal-import", importBacked: true, createdAt: new Date().toISOString() }]);
    }
    setRowSiteMappings((prev) => ({ ...prev, [value]: { action: "map", siteId, confidence: 100, auto: false, created: true } }));
    setSiteMappingSavedAt(new Date().toISOString());
  };
  const mappedSiteHeaders = detectedSiteHeaders.filter((header) => !unresolvedSiteHeaders.includes(header));
  const formatSiteLabel = (header) => labelFromSiteValue(header).replace(/ Store$/i, "");
  const renderSiteMappingCard = (header) => {
    const selected = siteMappings[header] || "";
    const selectedSite = siteOptions.find((site) => site.siteId === selected);
    const needsReview = !selected || selected === "__IGNORE__" || !selectedSite;

    return (
      <div className={`mc-siteMapCard ${needsReview ? "is-review" : "is-mapped"}`} key={header}>
        <div className="mc-siteMapCard__top">
          <div>
            <strong>{formatSiteLabel(header)}</strong>
            <span>{header}</span>
          </div>
          <div className={`mc-siteStatusPill ${needsReview ? "is-review" : "is-mapped"}`}>
            {selected === "__IGNORE__" ? "Ignored" : selectedSite ? "Mapped" : "Needs review"}
          </div>
        </div>

        <label className="mc-siteMapCard__label">Matched MC Site</label>
        <select
          className="mc-siteMapCard__select"
          value={selected}
          onChange={(event) => setSiteMappings((prev) => ({ ...prev, [header]: event.target.value }))}
        >
          <option value="">Select site</option>
          <option value="__IGNORE__">Ignore this column</option>
          {siteOptions.map((site) => (
            <option key={site.siteId} value={site.siteId}>{site.name || labelFromSiteValue(site.storeKey)}</option>
          ))}
        </select>

        <div className="mc-siteMapCard__foot">
          {selected === "__IGNORE__" ? "This column will be ignored." : selectedSite ? `Linked to ${selectedSite.name}.` : "Choose a site to save this alias."}
        </div>
      </div>
    );
  };
  const renderRowSiteCard = (value) => {
    const resolution = rowSiteMappings[value] || {};
    const selectedSite = siteOptions.find((site) => site.siteId === resolution.siteId);
    const needsReview = unresolvedRowSites.includes(value);
    return (
      <div className={`mc-siteMapCard ${needsReview ? "is-review" : "is-mapped"}`} key={value}>
        <div className="mc-siteMapCard__top"><div><strong>{value}</strong><span>CSV site value</span></div><div className={`mc-siteStatusPill ${needsReview ? "is-review" : "is-mapped"}`}>{resolution.action === "ignore" ? "Ignored" : selectedSite ? `${resolution.confidence || 100}% match` : "Needs review"}</div></div>
        <label className="mc-siteMapCard__label">MatrixOS site</label>
        <select className="mc-siteMapCard__select" value={resolution.action === "ignore" ? "__IGNORE__" : (resolution.siteId || "")} onChange={(event) => {
          const selected = event.target.value;
          setRowSiteMappings((prev) => ({ ...prev, [value]: selected === "__IGNORE__" ? { action: "ignore", siteId: "", confidence: 100 } : { action: selected ? "map" : "review", siteId: selected, confidence: selected ? siteSimilarity(value, siteOptions.find((site) => site.siteId === selected)) : 0, auto: false } }));
        }}>
          <option value="">Select existing site</option><option value="__IGNORE__">Ignore this source site</option>
          {siteOptions.map((site) => <option key={site.siteId} value={site.siteId}>{site.name || labelFromSiteValue(site.storeKey)}</option>)}
        </select>
        <div className="mc-siteMapCard__foot">{resolution.action === "ignore" ? "Rows for this source site will be excluded." : selectedSite ? `Mapped to ${selectedSite.name}. This alias will be remembered.` : "Map this value, create a new site, or ignore it."}</div>
        {!selectedSite && resolution.action !== "ignore" ? <button type="button" className="mc-engineBtn mc-engineBtn--secondary mc-createSiteBtn" onClick={() => createSiteFromRowValue(value)}>Create “{labelFromSiteValue(value)}”</button> : null}
      </div>
    );
  };

  const updateMapping = async (key, value) => {
    // Inventory quantity columns are never valid row-site identity fields. A stale
    // auto-map previously set Site / Store = inventory_<site>, which made stock
    // quantities (0, 1, 10, 117...) appear as dozens of fake sites.
    const detected = detectUniversalImportProfile(headers, engine.rawRows || [], engine?.config?.storeName || "");
    const safeValue = key === "site" && (detected.inventoryHeaders || []).includes(value) ? "" : value;
    // Mapping is operator-owned state. Do not pass it through recomputeEngineState:
    // that routine may rebuild mapping from auto-detection and briefly reset a
    // manually selected header back to the placeholder during React hydration.
    const nextCsvMapping = { ...(csvMapping || {}), [key]: safeValue };
    if ((detected.inventoryHeaders || []).length > 1 && (detected.inventoryHeaders || []).includes(nextCsvMapping.site)) {
      nextCsvMapping.site = "";
    }
    const isPosEngine = String(engine?.config?.dataSourceMode || "csv").toLowerCase() === "pos";
    const fields = { ...(detected.fields || {}) };
    Object.entries(nextCsvMapping).forEach(([field, header]) => {
      if (!header) return;
      fields[field] = { ...(fields[field] || {}), header, confidence: 100, source: "manual" };
    });
    const selectedSiteCandidate = nextCsvMapping.site || detected.rowSiteHeader || "";
    const selectedSiteHeader = (detected.inventoryHeaders || []).includes(selectedSiteCandidate) ? "" : selectedSiteCandidate;
    const layout = (detected.inventoryHeaders || []).length > 1 ? "column_site" : (selectedSiteHeader ? "row_site" : detected.layout);
    const unresolvedRequired = ["product", "systemSoh"].filter((field) => !(nextCsvMapping[field] || fields[field]?.header));
    if (layout === "row_site" && !selectedSiteHeader) unresolvedRequired.push("site");
    const recalculatedProfile = {
      ...detected,
      fields,
      rowSiteHeader: selectedSiteHeader,
      layout,
      unresolvedRequired,
      readyForNormalization: unresolvedRequired.length === 0,
      mappingConfirmedAt: new Date().toISOString(),
    };
    const now = new Date().toISOString();
    const rebuiltPreparedRows = !isPosEngine && recalculatedProfile.readyForNormalization
      ? buildPreparedData(engine.rawRows || [], nextCsvMapping, engine.config, engine.rules)
      : engine.preparedRows;
    const next = isPosEngine
      ? { ...engine, csvMapping: nextCsvMapping, importSourceProfile: recalculatedProfile, updatedAt: now }
      : {
          ...engine,
          mapping: nextCsvMapping,
          csvMapping: nextCsvMapping,
          preparedRows: rebuiltPreparedRows,
          importSourceProfile: recalculatedProfile,
          sourceLayout: layout,
          normalizationStatus: recalculatedProfile.readyForNormalization ? "ready" : "mapping_required",
          updatedAt: now,
        };
    setEngine(next);
    // Manual mapping must be durable authority, not a browser-only preference.
    // Persist the engine record immediately before updating the active snapshot.
    await saveEngineStateDurable(next, "ALL", { persistGroups: false, persistOwnership: false });
    if (!isPosEngine) await commitActiveInventorySnapshot("ALL", next);
  };

  const updatePosMapping = (key, value) => {
    const nextSettings = savePosSettings({
      fieldMapping: {
        ...(posSettings.fieldMapping || DEFAULT_POS_SETTINGS.fieldMapping),
        [key]: value,
      },
    }, "ALL");
    setPosSettings(nextSettings);

    if (String(engine?.config?.dataSourceMode || "").toLowerCase() === "pos") {
      const next = buildEngineFromPreparedInventory({ ...engine, csvMapping }, posInventory.rows || engine.rawRows || [], {
        provider: nextSettings.provider || "lightspeed",
        syncedAt: posInventory.syncedAt || engine.uploadedAt || new Date().toISOString(),
        scope: "ALL",
        posFieldMapping: nextSettings.fieldMapping || {},
      });
      setEngine(next);
      saveEngineState(next, "ALL");
    }
  };

  if (!canAccessAdminTabs(currentProfile)) {
    return (
      <div className="mc-enginePage mc-tabPage">
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        <McPageHeader activeKey="mapping" subtitle="This area is available to admin profiles only." />
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className="mc-enginePage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="mapping" />

      <section className="mc-enginePage__heroCards mc-enginePage__heroCards--four">
        <article className="mc-engineCard"><span>Mapped Fields</span><strong>{FIELD_LABELS.length}</strong></article>
        <article className="mc-engineCard"><span>Detected Headers</span><strong>{headers.length}</strong></article>
        <article className="mc-engineCard"><span>Source Layout</span><strong>{sourceProfile.layout === "row_site" ? "Row-based sites" : sourceProfile.layout === "column_site" ? "Site columns" : "Single site"}</strong></article>
        <article className="mc-engineCard"><span>Status</span><strong>{headers.length ? (sourceProfile.unresolvedRequired?.length || unresolvedSiteHeaders.length || unresolvedRowSites.length ? "Needs Review" : "Auto-mapped") : "Need CSV"}</strong></article>
      </section>

      <section className="mc-enginePage__content">
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>CSV to engine field map</h3>
              <p>Select which CSV header should feed each engine field.</p>
            </div>
            <div className="mc-engineBadge">CSV Mapping</div>
          </div>

          <div className="mc-fieldGrid">
            {FIELD_LABELS.map(([key, label]) => (
              <div className="mc-fieldControl" key={key}>
                <label>{label}</label>
                <select value={(csvMapping || {})[key] || ""} onChange={(e) => updateMapping(key, e.target.value)}>
                  <option value="">Select header</option>
                  {optionsForField(key).map((header) => <option key={header} value={header}>{header}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="mc-helperText">
            Morpheus now detects semantic header aliases and sample value shapes. Row-based Site columns and Lightspeed-style inventory_&lt;site&gt; columns are treated as different source layouts.
          </div>
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Source detection</h3>
              <p>The active Intake snapshot is now the source of truth for Mapping.</p>
            </div>
            <div className="mc-engineBadge">{sourceProfile.readyForNormalization ? "Ready" : "Review required"}</div>
          </div>
          <div className="mc-fieldGrid">
            <div className="mc-fieldControl">
              <label>Detected layout</label>
              <div className="mc-helperText">{sourceProfile.layout === "row_site" ? `Rows grouped by ${sourceProfile.rowSiteHeader}` : sourceProfile.layout === "column_site" ? `${sourceProfile.inventoryHeaders.length} inventory site columns` : "One site per file"}</div>
            </div>
            <div className="mc-fieldControl">
              <label>Normalization gate</label>
              <div className="mc-helperText">{sourceProfile.unresolvedRequired?.length ? `Unresolved: ${sourceProfile.unresolvedRequired.join(", ")}` : "Required fields detected"}</div>
            </div>
          </div>
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>POS to engine field map</h3>
              <p>Select which live POS field should feed each MC engine field. Lightspeed sends the fields; MC mapping decides what to use.</p>
            </div>
            <div className="mc-engineBadge">POS Mapping</div>
          </div>

          <div className="mc-fieldGrid">
            {FIELD_LABELS.map(([key, label]) => (
              <div className="mc-fieldControl" key={`pos-${key}`}>
                <label>{label}</label>
                <select value={hasPosSource ? ((posSettings.fieldMapping || {})[key] || "") : ""} onChange={(e) => updatePosMapping(key, e.target.value)}>
                  <option value="">Select POS field</option>
                  {optionsForPosField(key).map((header) => <option key={header} value={header}>{header}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="mc-helperText">
            Recommended Lightspeed defaults: Product = name, SKU = sku, Category = product_category, Cost = retail_price, System SOH = inventory_Main_Outlet. Supply price remains available as an explicit operator choice.
          </div>
        </article>

        <article className="mc-enginePanel mc-siteMappingPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>CSV site resolution</h3>
              <p>{sourceProfile.layout === "row_site" ? `Row-based source detected. ${detectedRowSiteValues.length} unique site values were found in ${sourceProfile.rowSiteHeader}.` : "Review inventory site columns once and Morpheus remembers them for future imports."}</p>
            </div>
            <div className={`mc-engineBadge ${unresolvedSiteHeaders.length ? "mc-engineBadge--blueAlert" : ""}`}>
              {sourceProfile.layout === "row_site" ? `${unresolvedRowSites.length} need review` : detectedSiteHeaders.length ? `${unresolvedSiteHeaders.length} need review` : "No CSV sites"}
            </div>
          </div>

          {sourceProfile.layout === "row_site" && detectedRowSiteValues.length ? (
            <>
              <div className={`mc-reviewBanner ${unresolvedRowSites.length ? "mc-reviewBanner--blue" : ""}`}><div><strong>{unresolvedRowSites.length ? `${unresolvedRowSites.length} source site${unresolvedRowSites.length === 1 ? "" : "s"} need a decision.` : "All source sites are resolved."}</strong><p>{unresolvedRowSites.length ? "Accept a suggested match, map to an existing MatrixOS site, create a new site, or ignore the source site." : "These aliases will be reused automatically on future imports from this source."}</p></div></div>
              {unresolvedRowSites.length ? <div className="mc-siteMappingSection"><div className="mc-siteMappingSection__title"><h4>Needs review</h4><span>{unresolvedRowSites.length}</span></div><div className="mc-siteMapGrid">{unresolvedRowSites.map(renderRowSiteCard)}</div></div> : null}
              <details className="mc-siteMappingDetails" open={!unresolvedRowSites.length}><summary><span>Resolved source sites</span><b>{mappedRowSites.length}</b></summary><div className="mc-siteMapGrid mc-siteMapGrid--mapped">{mappedRowSites.length ? mappedRowSites.map(renderRowSiteCard) : <div className="mc-emptyState"><strong>No resolved source sites yet.</strong><p>Complete the decisions above to make this import operational.</p></div>}</div></details>
              <div className="mc-siteMappingActionBar"><div><strong>Confirm site resolution</strong><span>Save value-to-site aliases and attach the resolution plan to the active import.</span></div><button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={saveRowSiteMappings} disabled={unresolvedRowSites.length > 0 || siteResolutionBusy}>{siteResolutionBusy ? "Reprocessing…" : "Confirm Site Resolution"}</button></div>
              {siteResolutionNotice ? <div className="mc-helperText">{siteResolutionNotice}</div> : siteMappingSavedAt ? <div className="mc-helperText">Site resolution saved to the active import.</div> : null}
            </>
          ) : detectedSiteHeaders.length ? (
            <>
              <div className={`mc-reviewBanner ${unresolvedSiteHeaders.length ? "mc-reviewBanner--blue" : ""}`}>
                <div>
                  <strong>{unresolvedSiteHeaders.length ? "CSV structure needs review." : "Site aliases are mapped."}</strong>
                  <p>
                    {unresolvedSiteHeaders.length
                      ? `${unresolvedSiteHeaders.length} inventory column${unresolvedSiteHeaders.length === 1 ? "" : "s"} need a site match before imports can be fully trusted.`
                      : "These mappings are saved separately from daily imports, so non-admin users cannot change them."}
                  </p>
                </div>
              </div>

              {unresolvedSiteHeaders.length ? (
                <div className="mc-siteMappingSection">
                  <div className="mc-siteMappingSection__title">
                    <h4>Needs review</h4>
                    <span>{unresolvedSiteHeaders.length}</span>
                  </div>
                  <div className="mc-siteMapGrid">
                    {unresolvedSiteHeaders.map(renderSiteMappingCard)}
                  </div>
                </div>
              ) : null}

              <details className="mc-siteMappingDetails" open={!unresolvedSiteHeaders.length}>
                <summary>
                  <span>Mapped columns</span>
                  <b>{mappedSiteHeaders.length}</b>
                </summary>
                <div className="mc-siteMapGrid mc-siteMapGrid--mapped">
                  {mappedSiteHeaders.length ? mappedSiteHeaders.map(renderSiteMappingCard) : (
                    <div className="mc-emptyState">
                      <strong>No mapped site columns yet.</strong>
                      <p>Once a CSV site column is matched, it will appear here.</p>
                    </div>
                  )}
                </div>
              </details>

              <div className="mc-siteMappingActionBar">
                <div>
                  <strong>Confirm site mapping</strong>
                  <span>Save these CSV column aliases so future imports use the same MC sites.</span>
                </div>
                <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={saveSiteMappings}>
                  Confirm Site Mapping
                </button>
              </div>
              {siteMappingSavedAt ? <div className="mc-helperText">Site mapping saved.</div> : null}
            </>
          ) : (
            <div className="mc-emptyState">
              <strong>No inventory site columns detected.</strong>
              <p>Upload a CSV first. Any new inventory_* columns will appear here for admin review.</p>
            </div>
          )}
        </article>
      </section>
      <McPageFooter />
    </div>
  );
}
